import { useState, useEffect, useRef } from 'react';
import { isUserSafari } from '../utils/safariUtils';
import { getWithExpiry, setWithExpiry } from '../utils/sessionStorage';
import { isUserFirefox } from '../utils/firefoxUtils';
import { S3UploadMetadata } from '../types/file';

export function useAudio() {
  const isSafari = isUserSafari();
  const fallbackGuid = getWithExpiry('applicantGuid');
  const [isAudioActive, setIsAudioActive] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // for stream
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [transcription, setTranscription] = useState<string | null>(null);

  // ref for stream
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  // audio devices
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState<string>(
    getWithExpiry('selectedAudioDeviceId') || ''
  );

  // audio level checking
  const [audioSilenceError, setAudioSilenceError] = useState<string | null>(null);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const AUDIO_LEVEL_THRESHOLD = 1;
  const NO_AUDIO_THRESHOLD = 10; // seconds


  // errors
  const [error, setError] = useState<string | null>(null);

  const monitorAudioSilence = () => {
    try{
      setAudioSilenceError(null);
      if (!analyserRef.current) return;
    
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    
      const checkSilence = () => {
        if (!analyserRef.current) return;
    
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAudioLevel(average);
    
        if (average >= AUDIO_LEVEL_THRESHOLD) {
          // Audio detected: clear silence timer if running
          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
          }
          setAudioSilenceError(null);
        } else {
          // No audio detected: start/restart silence timer
          if (!silenceTimeoutRef.current) {
            silenceTimeoutRef.current = setTimeout(() => {
            setAudioSilenceError(`We can't hear you, please double check your audio devices. (${Date.now()})`);
            }, NO_AUDIO_THRESHOLD * 1000);
          }
        }
    
        animationFrameRef.current = requestAnimationFrame(checkSilence);
      };
    
      checkSilence();

    }catch(error){
      console.error('Failed to monitor audio silence:', error);
      setAudioSilenceError(`Failed to monitor audio silence. Please check your microphone permissions and try again. (${Date.now()})`);
    }
    
  };


  useEffect(() => {
    // Set default audio device on initialization
    const initializeDefaultAudioDevice = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioDevices = devices.filter(device => device.kind === 'audioinput');
        
        if (audioDevices.length > 0) {
          // Get stored device ID if available
          const storedDeviceId = getWithExpiry('selectedAudioDeviceId');
          
          if (!storedDeviceId) {
            // No stored selection, use first device as default
            const defaultDeviceId = audioDevices[0].deviceId;
            setSelectedAudioDeviceId(defaultDeviceId);
            setWithExpiry('selectedAudioDeviceId', defaultDeviceId, 24 * 60 * 60 * 1000); // 24 hours
            console.log('Set default audio device:', defaultDeviceId);
          } else {
            // Check if the stored device is still available
            const deviceExists = audioDevices.some(device => device.deviceId === storedDeviceId);
            
            if (!deviceExists) {
              // Stored device not available, use first device instead
              const newDefaultId = audioDevices[0].deviceId;
              setSelectedAudioDeviceId(newDefaultId);
              setWithExpiry('selectedAudioDeviceId', newDefaultId, 24 * 60 * 60 * 1000);
              console.log('Stored audio device not found, using new default:', newDefaultId);
            }
          }
        }
      } catch (err) {
        console.error('Failed to set default audio device:', err);
      }
    };
    
    initializeDefaultAudioDevice();
  }, []);

  const getAudioDevices = async () => {
    try {
      setError(null);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        
        // Safari might need a small delay
        if (isUserSafari()) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      } catch (error) {
        console.log('Audio permission request failed:', error);
        setError(`Could not access microphone. Please check your browser permissions and try again. (${Date.now()})`);
        // reattempt permission request
        await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      const micDevices = devices.filter(device => device.kind === 'audioinput');
      
      // Only update if devices actually changed
      const devicesChanged = JSON.stringify(micDevices.map(d => d.deviceId)) !== 
                            JSON.stringify(audioDevices.map(d => d.deviceId));
      
      if (devicesChanged || audioDevices.length === 0) {
        console.log('Audio devices updated:', micDevices.length);
        setAudioDevices(micDevices);
      }
      
      return micDevices;
    } catch (err) {
      console.error('Failed to get audio devices:', err);
      setError(`Could not access microphone. Please check your browser permissions and try again. (${Date.now()})`);
      return [];
    }
  };

  // Effect to handle device selection when audioDevices change
  useEffect(() => {
    if (audioDevices.length > 0) {
      // Get stored device ID if available
      const storedDeviceId = getWithExpiry('selectedAudioDeviceId');
      
      if (!storedDeviceId) {
        // No stored selection, use first device as default
        const defaultDeviceId = audioDevices[0].deviceId;
        setSelectedAudioDeviceId(defaultDeviceId);
        setWithExpiry('selectedAudioDeviceId', defaultDeviceId, 24 * 60 * 60 * 1000); // 24 hours
        console.log('Set default audio device:', defaultDeviceId);
      } else {
        // Check if the stored device is still available
        const deviceExists = audioDevices.some(device => device.deviceId === storedDeviceId);
        
        if (!deviceExists) {
          // Stored device not available, use first device instead
          const newDefaultId = audioDevices[0].deviceId;
          setSelectedAudioDeviceId(newDefaultId);
          setWithExpiry('selectedAudioDeviceId', newDefaultId, 24 * 60 * 60 * 1000);
          console.log('Stored audio device not found, using new default:', newDefaultId);
        } else {
          // Use the stored device
          setSelectedAudioDeviceId(storedDeviceId);
        }
      }
    }
  }, [audioDevices]);

  // Load devices on initialization and listen for device changes
  useEffect(() => {
    // Initial load
    getAudioDevices();

    // Listen for device changes (plugging/unplugging devices)
    const handleDeviceChange = () => {
      console.log('Audio device change detected, refreshing audio devices...');
      getAudioDevices();
    };

    // Add event listener for device changes
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    // Cleanup event listener on unmount
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  const startAudio = async (deviceId?: string) => {
    try {
      setError(null);
      console.log('Starting audio with deviceId:', deviceId);
      if (!deviceId) {
        deviceId = getWithExpiry('selectedAudioDeviceId');
        
        // If still no device ID, ensure we have devices enumerated
        if (!deviceId && audioDevices.length === 0) {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const micDevices = devices.filter(device => device.kind === 'audioinput');
          
          if (micDevices.length > 0) {
            deviceId = micDevices[0].deviceId;
            // Store this default selection
            setWithExpiry('selectedAudioDeviceId', deviceId, 24 * 60 * 60 * 1000);
          }
        } else if (!deviceId && audioDevices.length > 0) {
          // Use first available device if we have the list but no selection
          deviceId = audioDevices[0].deviceId;
          // Store this default selection
          setWithExpiry('selectedAudioDeviceId', deviceId, 24 * 60 * 60 * 1000);
        }
      }
      console.log('deviceId', deviceId);
      const constraints = {
        audio: deviceId ? { deviceId: { exact: deviceId } } : true
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // Update selectedAudioDeviceId if a new one was used
      if (deviceId && deviceId !== selectedAudioDeviceId) {
        setSelectedAudioDeviceId(deviceId);
      }

      // Create audio context for visualization
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      // Start monitoring audio levels
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const checkAudioLevel = () => {
        if (!analyserRef.current) return;

        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAudioLevel(average);

        animationFrameRef.current = requestAnimationFrame(checkAudioLevel);
      };

      checkAudioLevel();
      setIsAudioActive(true);
      return true;
    } catch (error) {
      console.error('Error accessing microphone:', error);
      return false;
    }
  };

  

  const stopAudio = () => {
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
    setAudioSilenceError(null);

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    setIsAudioActive(false);
    setAudioLevel(0);
  };

  const startStreamedAudioRecording = async (
    applicantGuid: string = fallbackGuid,
    questionIndex: number, 
    questionText: string, 
    isLastQuestion: boolean = false,
    totalQuestions: number,
    s3MetaData: S3UploadMetadata
  ) => {
    try {
      // Reset state
      setRecordingError(null);
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
      
      setIsRecording(false);
      setProgress(0);
      
      const constraints = {
        audio: selectedAudioDeviceId ? { deviceId: { exact: selectedAudioDeviceId } } : true
      };

      console.log('Constraints:', constraints);
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      await monitorAudioSilence();
      
      // Create MediaRecorder with proper options 
      // safari supports mp4, but not webm
      const options = { 
        mimeType: isSafari ? 'audio/mp4' : 'audio/webm',
        audioBitsPerSecond: 128000 
      };
      console.log('Using MediaRecorder options:', options);
      const recorder = new MediaRecorder(stream, options);
      
      // Set up WebSocket connection
      const socketUrl = getWebSocketUrl();
      console.log('Connecting to WebSocket:', socketUrl);
      const ws = new WebSocket(socketUrl);
      wsRef.current = ws;
      
      // Track socket state and local recording
      let socketConnected = false;
      let recordingStarted = false;
      const chunks: BlobPart[] = [];
      
      // Handle WebSocket connection open
      ws.onopen = () => {
        console.log('WebSocket connection established');
        socketConnected = true;

        // Send metadata before starting recording
      ws.send(JSON.stringify({
        type: 'metadata',
        applicantGuid,
        questionId: questionIndex,
        questionText,
        isLastQuestion,
        totalQuestions,
        s3MetaData
      }));

      setIsRecording(true);
      if (!recordingStarted) {
        recorder.start(1000); // Send data every 1 second
        recordingStarted = true;
        console.log('MediaRecorder started');
      }
      };
      
      // Handle WebSocket errors
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setRecordingError(`Connection error. Please try again. (${Date.now()})`);
        
        if (recorder.state === 'recording') recorder.stop();
        stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
      };
      
      // Handle WebSocket closure
      ws.onclose = () => {
        console.log('WebSocket connection closed');
        socketConnected = false;
      };

      // Stream data chunks as they become available
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          // Store chunk locally for replay
          chunks.push(event.data);
          
          // Send chunk to server if socket is connected
          if (socketConnected) {
            console.log(`Sending audio chunk: ${Math.round(event.data.size / 1024)} KB`);
            ws.send(event.data);
          }
        }
      };
      
      // Handle recording stop
      recorder.onstop = () => {
        console.log('MediaRecorder stopped');
        
        // Create local audio blob from collected chunks
        const mimeType = options.mimeType;
        const audioBlob = new Blob(chunks, { type: mimeType });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        // Save the local recording reference
        setRecordedBlob(audioBlob);
        setRecordedAudioUrl(audioUrl);
        
        // Handle WebSocket messages for processing results
        ws.onmessage = (event) => {
          console.log('WebSocket message data:', event.data);
          
          try {
            const parsedData = JSON.parse(event.data);
            
            if (parsedData.type === 'upload_complete') {
              // For audio, we only need audioUrl and transcription
              setAudioUrl(parsedData.audioUrl);
              setTranscription(parsedData.transcription);
              setProgress(100);
            } else if (parsedData.type === 'error') {
              console.error('Server error:', parsedData.message);
              setRecordingError(`Something went wrong. Please try again. (${Date.now()})`);
            }else if (parsedData.type === 'metadata_received') {
              console.log('Server received metadata');
            } else if (parsedData.type === 'answer_submitted') {
              console.log('Answer submitted to API', parsedData.isReconciled);
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };
        
        // Clean up media stream
        stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
      };
      
      // Store recorder and stream for later access
      mediaRecorderRef.current = recorder;
      audioStreamRef.current = stream;
      
      // If socket connected before recorder was ready
      if (socketConnected && !recordingStarted) {
        recorder.start(1000);
        recordingStarted = true;
        setIsRecording(true);
      }
      
    } catch (error) {
      console.error('Failed to start streaming audio recording:', error);
      setRecordingError(`Recording failed. Please check your microphone permissions and try again. (${Date.now()})`);
      throw error;
    }
  };

  const stopStreamedAudioRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      console.log('Stopping streamed audio recording');
      mediaRecorderRef.current.stop();

      setTimeout(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          console.log("User has stopped recording");
        }
      }, 500); // Small delay to ensure all chunks are sent
    }

    
  };

  // this will be used to submit the recording to the server
  const handleSubmitStreamRecording = () => {
    try{
      setError(null);
      setRecordingError(null);
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'process_recording',
        }));
        console.log('Sent process_recording signal to server');
      }
      else {
        console.error('Cannot submit recording: WebSocket is not connected');
      }
    }catch(error){
      console.error('Failed to submit streaming audio recording:', error);
      setRecordingError(`Failed to submit recording. Please check your microphone or internet connection and try again. (${Date.now()})`);
      throw error;
    }
    
  };

  const getWebSocketUrl = () => {
    // Check if we're in development (localhost)
    const isDev = 
      window.location.hostname === 'localhost' || 
      window.location.hostname === '127.0.0.1';
    
    if (isDev) {
      // Development: Use port 3001 (assuming your WebSocket server runs on 3001)
      return `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.hostname}:3001/stream-audio`;
    } else {
      // Production: Use same host without port manipulation
      // WebSockets will be proxied at the infrastructure level
      return `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/stream-audio`;
    }
  };

  const toggleAudio = async (deviceId?: string) => {
    try{
      setError(null);
      if (isAudioActive) {
        stopAudio();
        return false;
      } else {
        console.log('Starting audio with deviceId:', deviceId || selectedAudioDeviceId);
        return await startAudio(deviceId || selectedAudioDeviceId);
      }
    } catch (error) { 
      console.error('Failed to toggle audio:', error);
      setError(`Could not access microphone. Please check your browser permissions and try again. (${Date.now()})`);
      return null;
    }
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, []);

  return {
    isAudioActive,
    audioLevel,
    startAudio,
    stopAudio,
    toggleAudio,
    isRecording,
    setIsRecording,
    recordingError,
    progress,
    recordedBlob,
    recordedAudioUrl,
    audioUrl,
    transcription,
    resetAudioAndTranscription: () => {
      setAudioUrl(null);
      setTranscription(null);
    },
    startStreamedAudioRecording,
    stopStreamedAudioRecording,
    audioDevices,
    selectedAudioDeviceId,
    setSelectedAudioDeviceId,
    getAudioDevices,
    handleSubmitStreamRecording,
    error,
    audioSilenceError,
  };
}
